import { convertRegionsFromDto, getStatusByCampaigns } from 'utils/campaign';
import { CampaignDTO, mappingCampaignStateFromDTO } from 'utils/campaign/mappers';
import { sortAreas, sortProfRoles } from 'utils/clickme';

import urlParser from 'bloko/common/urlParser';

import { Requirements } from 'app/routes';

import {
    Amounts,
    CountsCampaign,
    PROF_ROLES,
    ProfRoles,
    RegionBE,
    setAmounts,
    setCountsCampaign,
    setCurrentStatus,
    setLeadsCount,
    setPage,
    setProfRoles,
    setRegions,
} from 'store/autoReducers';
import { Action } from 'store/createReducer';
import {
    CampaignTabStatus,
    setCampaigns,
    setCampaignSearchValue,
    setCheckedCampaignIds,
    setIsCampaignSearchUploaded,
    setLoadCampaigns,
} from 'store/models/dashboard';

const DEFAULT_PAGE = 1;

const NAME_FILTER_PARAM = 'nameFilter';

const DashboardRequirements = (
    tab: CampaignTabStatus
): Requirements<{
    count: CountsCampaign;
    campaigns: CampaignDTO[];
    regions: RegionBE[];
    profRoles: { professionalCategories: ProfRoles[] };
    leads: {
        count: number;
    };
    depositAmount: Amounts;
}> => {
    return {
        methods: ({ state }) => {
            const { campaignSearchValue, appliedFilters } = state.dashboard;
            const searchValue = state.router?.location?.query?.[NAME_FILTER_PARAM] || campaignSearchValue;

            const campaignsQueryStr = urlParser.stringify({
                pageType: tab,
                nameFilter: searchValue,
                page: DEFAULT_PAGE,
                productType: appliedFilters.productTypes || null,
                campaignStatus: appliedFilters.campaignStatuses || null,
                budgetStatus: appliedFilters.budgetStatuses || null,
                moderationStatus: appliedFilters.moderationStatuses || null,
                area: appliedFilters.areas || null,
                professionalRoles: appliedFilters.professionalRoles || null,
                createdByUserIds: appliedFilters.createdByUserIds || null,
            });

            return {
                campaigns: {
                    url: `/api/v1/campaigns/?${campaignsQueryStr}`,
                    cacheable: false,
                },
                regions: { url: '/api/v1/catalog/areas' },
                leads: { url: '/api/v1/leads/count' },
                depositAmount: { url: '/api/v1/catalog/depositAmount' },
            };
        },
        additionalMethods: ({ state }) => {
            const { campaignSearchValue, appliedFilters } = state.dashboard;
            const searchValue = state.router?.location?.query?.[NAME_FILTER_PARAM] || campaignSearchValue;

            const countQueryStr = urlParser.stringify({
                nameFilter: searchValue,
                productType: appliedFilters.productTypes || null,
                campaignStatus: appliedFilters.campaignStatuses || null,
                budgetStatus: appliedFilters.budgetStatuses || null,
                moderationStatus: appliedFilters.moderationStatuses || null,
                area: appliedFilters.areas || null,
                professionalRoles: appliedFilters.professionalRoles || null,
                createdByUserIds: appliedFilters.createdByUserIds || null,
            });

            return {
                count: { url: `/api/v1/account/campaigns/count?${countQueryStr}`, cacheable: false },
                [PROF_ROLES]: { url: '/api/v1/catalog/professionalCatalog' },
            };
        },
        isDataReady: ({ result }) => {
            return !!result.count;
        },
        resolve: ({ result: { count, campaigns, regions, [PROF_ROLES]: profRoles, leads, depositAmount }, state }) => {
            const pageActions: Action[] = [
                setCountsCampaign(count),
                setLoadCampaigns(getStatusByCampaigns(campaigns)),
                setPage(DEFAULT_PAGE),
                setCampaigns(campaigns.map(mappingCampaignStateFromDTO)),
                setCheckedCampaignIds({}),
                setCurrentStatus(tab),
                setRegions(sortAreas(convertRegionsFromDto(regions))),
                setLeadsCount(leads.count),
                setAmounts(depositAmount),
            ];

            if (profRoles) pageActions.push(setProfRoles(sortProfRoles(profRoles.professionalCategories)));

            const query = state?.router?.location?.query;
            if (query?.[NAME_FILTER_PARAM]) {
                pageActions.push(setCampaignSearchValue(query[NAME_FILTER_PARAM]), setIsCampaignSearchUploaded(true));
            }

            return pageActions;
        },
    };
};

export default DashboardRequirements;
